// Pure helpers extracted from PublicViewer.jsx during the iter-38 refactor.
// Keeping these in a dedicated file lets the giant PublicViewer stay
// focused on the view/state machine and gives us a natural home for future
// scrub/transition maths.

/** Map from a swipe direction to its opposite. Powers implicit reverse
 *  transitions (a "left" swipe going forward is a "right" swipe coming back). */
export const OPPOSITE = { left: "right", right: "left", up: "down", down: "up", none: "none" };

/** Which pointer axis owns each swipe direction — used by the scrub gesture
 *  detector to decide whether a drag maps into horizontal (`x`) or vertical
 *  (`y`) progress space. */
export const AXIS_OF = { left: "x", right: "x", up: "y", down: "y" };

/** Pixel distance required to confirm a drag is intentional (vs a tap). This
 *  is intentionally tiny — the 3D-rotation feel requires the scrub overlay to
 *  engage on the very first touch movement. */
export const DRAG_INTENT_PX = 6;

/** ms to wait before showing the "Tap to begin" overlay if autoplay was
 *  denied. Long enough to cover slow Wi-Fi initial-load, short enough to
 *  feel snappy. */
export const AUTOPLAY_SNIFF_MS = 1400;

/** iter28 bidirectional default: any transition that has a media URL AND is
 *  scrub/auto-video is treated as `same_media` by default UNLESS the admin
 *  explicitly set `reverse_mode='one_way'` or `reverse_mode='separate_media'`.
 *  This matches the brief: "support B→A using the same video in reverse
 *  unless explicitly configured otherwise". */
export function transitionHasImplicitReverse(t) {
  if (t.reverse_mode === "same_media" || t.reverse_mode === "bidirectional") return true;
  if (t.reverse_mode === "one_way" || t.reverse_mode === "separate_media") return false;
  // No explicit setting → infer: only if we actually have media to reverse.
  if (!t.transition_media_url) return false;
  return t.scrubbable_enabled || t.transition_type === "auto_video";
}

/** Build the effective list of transitions from a scene, including virtual
 *  reverses synthesized from outgoing transitions on other scenes. Explicit
 *  transitions always win over synthesized reverses. */
export function buildEffectiveTransitions(allTransitions, fromSceneId) {
  const out = [];
  const hasExplicit = new Set();
  for (const t of allTransitions) {
    if (t.from_scene_id === fromSceneId) {
      out.push({ ...t, _reverse: false });
      hasExplicit.add(t.to_scene_id);
    }
  }
  for (const t of allTransitions) {
    if (t.to_scene_id === fromSceneId && transitionHasImplicitReverse(t)) {
      if (hasExplicit.has(t.from_scene_id)) continue; // explicit reverse takes priority
      out.push({
        ...t,
        id: `${t.id}__rev`,
        _reverse: true,
        from_scene_id: t.to_scene_id,
        to_scene_id: t.from_scene_id,
        swipe_direction: OPPOSITE[t.swipe_direction] || "none",
        button_label: t.button_label ? null : null,
      });
    }
  }
  return out.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
}

/** Fit an intrinsic media (mw × mh) into a container (cw × ch) using either
 *  'cover' or 'contain' semantics. Mirrors CSS's object-fit but returns
 *  numeric scale + offsets so we can map zone polygon coordinates into the
 *  same screen space the video/img is actually rendered in. */
export function computeFit(mw, mh, cw, ch, mode) {
  if (!mw || !mh || !cw || !ch) return { scale: 1, offsetX: 0, offsetY: 0 };
  const sCover = Math.max(cw / mw, ch / mh);
  const sContain = Math.min(cw / mw, ch / mh);
  const scale = mode === "contain" ? sContain : sCover;
  const rw = mw * scale, rh = mh * scale;
  return { scale, offsetX: (cw - rw) / 2, offsetY: (ch - rh) / 2 };
}
